import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api } from '@api'
import { secureTokenStore } from '@store'
import { type AdminEventsFilters, toAdminEventsQuery } from '@utils'
import type {
  AdminEvent,
  AdminEventDetail,
  AdminEventFilter,
  AdminOrganizer,
  AdminOrganizerDetail,
  AdminOrganizerStatus,
  AdminOverview,
  AdminPage,
  AdminSettings,
  OrganizerCandidate,
  OrganizerInvitation,
  AdminRequestsResponse,
  OrganizerBilling,
  OrganizerRequest,
} from './types'

/** Root key shared by every super-admin query. */
export const ADMIN_QUERY_KEY = 'admin-organizers'

const isLoggedIn = () => !!secureTokenStore.getAccessToken()

/** Pending requests (or another status), with the pending total for the menu badge. */
export const useAdminOrganizerRequests = (
  options: { status?: 'pending' | 'approved' | 'rejected' | 'all'; limit?: number; enabled?: boolean } = {},
) => {
  const { status = 'pending', limit = 50, enabled = true } = options
  return useQuery<AdminRequestsResponse>({
    queryKey: [ADMIN_QUERY_KEY, 'requests', status, limit],
    queryFn: async () =>
      (await api.get<AdminRequestsResponse>('/admin/organizer-requests', { params: { status, limit } }))
        .data,
    enabled: enabled && isLoggedIn(),
    // Keeps the menu badge fresh without a manual refresh.
    refetchInterval: 60_000,
  })
}

export const useAdminOrganizers = (search: string, status: AdminOrganizerStatus = 'active') =>
  useQuery<AdminPage<AdminOrganizer>>({
    queryKey: [ADMIN_QUERY_KEY, 'list', search, status],
    queryFn: async () =>
      (
        await api.get<AdminPage<AdminOrganizer>>('/admin/organizers', {
          params: { search: search || undefined, status, limit: 100 },
        })
      ).data,
    enabled: isLoggedIn(),
    placeholderData: keepPreviousData,
  })

export const useAdminOrganizer = (accountId: string | undefined) =>
  useQuery<AdminOrganizerDetail>({
    queryKey: [ADMIN_QUERY_KEY, 'detail', accountId],
    queryFn: async () =>
      (await api.get<AdminOrganizerDetail>(`/admin/organizers/${accountId}`)).data,
    enabled: !!accountId && isLoggedIn(),
  })

/** Key figures of the whole platform (one aggregate call). */
export const useAdminOverview = () =>
  useQuery<AdminOverview>({
    queryKey: [ADMIN_QUERY_KEY, 'overview'],
    queryFn: async () => (await api.get<AdminOverview>('/admin/overview')).data,
    enabled: isLoggedIn(),
  })

export const ADMIN_EVENTS_PAGE_SIZE = 20

export const useAdminEvents = (filters: AdminEventsFilters) =>
  useQuery<AdminPage<AdminEvent>>({
    queryKey: [ADMIN_QUERY_KEY, 'events', toAdminEventsQuery(filters, ADMIN_EVENTS_PAGE_SIZE)],
    queryFn: async () =>
      (
        await api.get<AdminPage<AdminEvent>>('/admin/events', {
          params: toAdminEventsQuery(filters, ADMIN_EVENTS_PAGE_SIZE),
        })
      ).data,
    enabled: isLoggedIn(),
    placeholderData: keepPreviousData,
    // A bad period (from > to) answers 400: nothing to retry.
    retry: false,
  })

/** Accounts matching a name or an email (the "Add an organizer" search). */
export const useAdminOrganizerCandidates = (search: string) =>
  useQuery<{ data: OrganizerCandidate[] }>({
    queryKey: [ADMIN_QUERY_KEY, 'candidates', search],
    queryFn: async () =>
      (
        await api.get<{ data: OrganizerCandidate[] }>('/admin/organizer-candidates', {
          params: { search, limit: 10 },
        })
      ).data,
    enabled: search.length >= 2 && isLoggedIn(),
    placeholderData: keepPreviousData,
  })

export const useAdminOrganizerInvitations = () =>
  useQuery<AdminPage<OrganizerInvitation>>({
    queryKey: [ADMIN_QUERY_KEY, 'invitations'],
    queryFn: async () =>
      (
        await api.get<AdminPage<OrganizerInvitation>>('/admin/organizer-invitations', {
          params: { limit: 50 },
        })
      ).data,
    enabled: isLoggedIn(),
  })

export const useAdminEvent = (eventId: string | undefined) =>
  useQuery<AdminEventDetail>({
    queryKey: [ADMIN_QUERY_KEY, 'event', eventId],
    queryFn: async () => (await api.get<AdminEventDetail>(`/admin/events/${eventId}`)).data,
    enabled: !!eventId && isLoggedIn(),
    retry: false,
  })

export const useAdminSettings = () =>
  useQuery<AdminSettings>({
    queryKey: [ADMIN_QUERY_KEY, 'settings'],
    queryFn: async () => (await api.get<AdminSettings>('/admin/settings')).data,
    enabled: isLoggedIn(),
  })

export const useOrganizerBilling = (accountId: string | undefined, enabled = true) =>
  useQuery<OrganizerBilling>({
    queryKey: [ADMIN_QUERY_KEY, 'billing', accountId],
    queryFn: async () =>
      (await api.get<OrganizerBilling>(`/admin/organizers/${accountId}/billing`)).data,
    // A removed organizer has no billing (the API answers 404).
    enabled: enabled && !!accountId && isLoggedIn(),
    retry: false,
  })

/** My own latest request to become an organizer (null = never asked). */
export const useMyOrganizerRequest = (enabled = true) =>
  useQuery<{ request: OrganizerRequest | null }>({
    queryKey: [ADMIN_QUERY_KEY, 'mine'],
    queryFn: async () =>
      (await api.get<{ request: OrganizerRequest | null }>('/me/organizer-request')).data,
    enabled: enabled && isLoggedIn(),
  })

/**
 * Drives the "Administration" menu entry: shown only to a super-admin, with
 * the number of organizer requests waiting for validation as its badge.
 */
export const useAdminMenu = () => {
  const mine = useQuery<{ isSuperAdmin?: boolean }>({
    queryKey: ['events', 'mine'],
    queryFn: async () => (await api.get<{ isSuperAdmin?: boolean }>('/me/events')).data,
    enabled: isLoggedIn(),
    select: (data) => ({ isSuperAdmin: data.isSuperAdmin === true }),
  })
  const isSuperAdmin = mine.data?.isSuperAdmin === true
  const requests = useAdminOrganizerRequests({ limit: 1, enabled: isSuperAdmin })
  return {
    isSuperAdmin,
    /** True until we know whether the account is a super-admin. */
    isLoading: mine.isLoading,
    pendingCount: isSuperAdmin ? (requests.data?.pendingCount ?? 0) : 0,
  }
}
